import React from "react";
import { BrowserRouter, Route, Switch, useLocation } from "react-router-dom";

import "./App.scss";
import Footer from "components/Footer";
import Navbar from "components/Navbar/Navbar";
import Seo from "components/Seo";
import { COLOR_MODES, DEFAULT_COLOR_MODE, getThemeByName, isColorMode } from "lib/data/themes";
import Contact from "pages/Contact";
import HomePage from "pages/HomePage";
import OurTeam from "pages/OurTeam";
import PortfolioDetail from "pages/PortfolioDetail";
import QuivioEnclave from "pages/QuivioEnclave";
import Services from "pages/Services";
import Works from "pages/Works";
import ThankYou from "pages/ThankYou";
import Blog from "pages/Blog";
import BlogPost from "pages/BlogPost";
import { useTranslation } from "lib/i18n";

const getInitialColorMode = () => {
  if (typeof window === "undefined") {
    return DEFAULT_COLOR_MODE;
  }

  const storedColorMode = window.localStorage.getItem("quivio-color-mode");
  if (isColorMode(storedColorMode)) {
    return storedColorMode;
  }

  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  ) {
    return COLOR_MODES.LIGHT;
  }

  return DEFAULT_COLOR_MODE;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (process.env.NODE_ENV === "test") {
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App = () => {
  const { t } = useTranslation();
  // Use PUBLIC_URL for subdirectory deployments; avoid deriving from the current path.
  const basename = process.env.PUBLIC_URL || "/";
  const activeTheme = getThemeByName(process.env.REACT_APP_THEME);
  const [colorMode, setColorMode] = React.useState(getInitialColorMode);
  const colorModeClassName = `color-mode-${colorMode}`;
  const appClassName = ["App", activeTheme.appClassName, colorModeClassName]
    .filter(Boolean)
    .join(" ");
  const activeThemeColor =
    (activeTheme.themeColors && activeTheme.themeColors[colorMode]) ||
    activeTheme.themeColor;

  const handleToggleColorMode = () => {
    setColorMode((currentColorMode) =>
      currentColorMode === COLOR_MODES.DARK ? COLOR_MODES.LIGHT : COLOR_MODES.DARK
    );
  };

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const themeColor = document.querySelector('meta[name="theme-color"]');

    if (activeTheme.bodyClassName) {
      document.body.classList.add(activeTheme.bodyClassName);
    }
    document.body.classList.add(colorModeClassName);
    document.documentElement.setAttribute("data-color-mode", colorMode);

    if (themeColor) {
      themeColor.setAttribute("content", activeThemeColor);
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem("quivio-color-mode", colorMode);
    }

    return () => {
      if (activeTheme.bodyClassName) {
        document.body.classList.remove(activeTheme.bodyClassName);
      }
      document.body.classList.remove(colorModeClassName);
      document.documentElement.removeAttribute("data-color-mode");

      if (themeColor) {
        themeColor.setAttribute("content", "#000000");
      }
    };
  }, [activeTheme, activeThemeColor, colorMode, colorModeClassName]);

  return (
    <div className={appClassName}>
      <BrowserRouter basename={basename}>
        <ScrollToTop />
        <Seo />
        <a className="skip-link" href="#main-content">
          {t("misc.skip")}
        </a>
        <Navbar colorMode={colorMode} onToggleColorMode={handleToggleColorMode} />
        <main id="main-content" className="main-content" tabIndex="-1">
          <Switch>
            <Route exact path="/" component={HomePage} />
            <Route exact path="/contact" component={Contact} />
            <Route exact path="/services" component={Services} />
            <Route exact path="/quivio-enclave" component={QuivioEnclave} />
            <Route exact path="/works" component={Works} />
            <Route exact path={"/works/:id"} component={PortfolioDetail} />
            <Route exact path="/blog" component={Blog} />
            <Route exact path="/blog/:slug" component={BlogPost} />
            <Route exact path={"/team"} component={OurTeam} />
            <Route exact path={"/thank-you"} component={ThankYou} />
          </Switch>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
};

export default App;
